import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import { AdminService, AffarspartnerSummary } from '@/services/admin.service';
import { ApiError } from '@/services/api';
import { CacheManager, CACHE_KEYS } from '@/utils/cache';

interface AllowedAffarspartnersSectionProps {
  controlTypeId: string;
}

/**
 * Admin allowlist editor for a single kontrolltyp.
 *
 * The master affärspartner table holds ~4000 rows, so a global picker is out.
 * Instead the admin types an orgnr or affärspartner id, hits Add (or Enter),
 * and the entry is resolved server-side and appended. The list is replaced
 * atomically on Save. An empty list means "open to all".
 */
const AllowedAffarspartnersSection: React.FC<AllowedAffarspartnersSectionProps> = ({
  controlTypeId,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [selected, setSelected] = useState<AffarspartnerSummary[]>([]);
  const [initial, setInitial] = useState<AffarspartnerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    AdminService.getAllowedAffarspartners(controlTypeId)
      .then((current) => {
        if (cancelled) return;
        setSelected(current);
        setInitial(current);
      })
      .catch((err) => {
        if (cancelled) return;
        enqueueSnackbar('Kunde inte ladda behörighetslistan', { variant: 'error' });
        console.error(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [controlTypeId, enqueueSnackbar]);

  const isDirty = useMemo(() => {
    if (selected.length !== initial.length) return true;
    const initialIds = new Set(initial.map((a) => a.id));
    return selected.some((a) => !initialIds.has(a.id));
  }, [selected, initial]);

  const handleAdd = async () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    if (selected.some((a) => a.id === trimmed || a.orgnr === trimmed)) {
      enqueueSnackbar('Affärspartnern finns redan i listan', { variant: 'info' });
      setQuery('');
      return;
    }

    setAdding(true);
    try {
      const match = await AdminService.lookupAffarspartner(trimmed);
      // Re-check by id in case the user typed an orgnr that resolves to a row already added.
      if (selected.some((a) => a.id === match.id)) {
        enqueueSnackbar('Affärspartnern finns redan i listan', { variant: 'info' });
      } else {
        setSelected((prev) => [...prev, match]);
      }
      setQuery('');
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 404) {
        enqueueSnackbar(`Ingen affärspartner matchar "${trimmed}"`, { variant: 'error' });
      } else {
        enqueueSnackbar('Kunde inte hämta affärspartnern', { variant: 'error' });
        console.error(err);
      }
    } finally {
      setAdding(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleAdd();
    }
  };

  const handleRemove = (id: string) => {
    setSelected((prev) => prev.filter((a) => a.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await AdminService.setAllowedAffarspartners(
        controlTypeId,
        selected.map((a) => a.id),
      );
      setSelected(updated);
      setInitial(updated);
      // Invalidate the lantbrukare-side cache so a tab open during the change
      // refreshes its kontrolltyp catalog on next load.
      //
      // CACHE_KEYS today defines PREDEFINED_CONTROL_TYPES (used by the
      // catalog list) and CONTROL_TYPE_DETAILS (declared but not yet wired
      // up anywhere — verified via grep). If a future change starts caching
      // single-kontrolltyp responses under CONTROL_TYPE_DETAILS, add the
      // matching CacheManager.remove(...) call here.
      CacheManager.remove(CACHE_KEYS.PREDEFINED_CONTROL_TYPES);
      enqueueSnackbar(
        updated.length === 0
          ? 'Behörighet rensad — alla har tillgång'
          : 'Behörighetslistan har sparats',
        { variant: 'success' },
      );
    } catch (err) {
      enqueueSnackbar('Kunde inte spara behörighetslistan', { variant: 'error' });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1
        }}>
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Laddar behörighetslistan…
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Behörighet — affärspartners
      </Typography>
      <FormHelperText sx={{ mb: 1 }}>
        Lämna tom om alla lantbrukare ska kunna se kontrolltypen. Lägg till
        affärspartners (via orgnr eller affärspartner-id) för att begränsa
        till lantbrukare där aktiv gård innehåller minst en av dessa.
      </FormHelperText>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          mb: 2
        }}>
        <TextField
          fullWidth
          size="small"
          label="Orgnr eller affärspartner-id"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={adding}
        />
        <Button
          variant="outlined"
          onClick={handleAdd}
          disabled={!query.trim() || adding}
          startIcon={adding ? <CircularProgress size={16} /> : null}
        >
          Lägg till
        </Button>
      </Stack>

      {selected.length === 0 ? (
        <Chip label="Alla har tillgång" variant="outlined" />
      ) : (
        <Box
          sx={{
            maxHeight: 280,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <List dense disablePadding>
            {selected.map((ap) => (
              <ListItem
                key={ap.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label={`Ta bort ${ap.name}`}
                    onClick={() => handleRemove(ap.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <ListItemText
                  primary={ap.name}
                  secondary={`${ap.orgnr} · ${ap.id}`}
                />
              </ListItem>
            ))}
          </List>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: 'block',
              textAlign: 'right',
              px: 1,
              py: 0.5
            }}>
            {selected.length} affärspartner{selected.length === 1 ? '' : 's'}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1
        }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isDirty || saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Sparar…' : 'Spara behörigheter'}
        </Button>
      </Box>
    </Box>
  );
};

export default AllowedAffarspartnersSection;
